import { breakpoints, colors, fonts } from '../styles';
import useEventListener from '../utils/useEventListener';
import useViewport from '../utils/useViewport';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

interface AnswerButtonProps {
    index?: any;
    level?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    answerLevel?: any;
    shortcut: string;
    color?: string;
    backgroundType: 'content' | 'shortcut';
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    className?: string;
    children?: React.ReactNode;
    tooltipTextColor?: string;
    to?: string;
    levels_state?: any;
    loading?: boolean;
    cursor?: boolean;
    emailAuth?: any;
    showShortcut: boolean;
}

interface AnswerButtonWrapperCSSProps {
    color: string;
    showBorder: boolean;
    padding: boolean;
    shouldShowCursor: boolean;
}

interface ShortcutIndicatorCSSProps {
    color: string;
    showBackground: boolean;
    textColor: string;
    margin: boolean;
}

interface LoadingIndicatorCSSProps {
    show: boolean;
}

const AnswerButtonWrapper = styled.div<AnswerButtonWrapperCSSProps>`
    display: flex;
    justify-content: space-between;
    height: 100%;
    
    ${breakpoints.onlyMobile} {
        width: 100%;
        padding-left: 10px;
    }
    box-sizing: border-box;
    border: solid 2px
        ${(props) => (props.showBorder ? '#e6e6e6' : 'transparent')};
    border-radius: 3px;
    padding: ${(props) => (props.padding ? 15 : 0)}px;
    align-items: center;
    background-color: ${(props) => props.color};
    cursor: ${(props) => (props.shouldShowCursor ? 'pointer' : 'inherit')};
    position: relative;
    z-index: 3;
    width: 100%;
    &:hover {
        border: solid 2px
            ${(props) => (props.showBorder ? '#c4bdbd' : 'transparent')};
    }
`;

const Text = styled.div`
    color: ${colors.libertyBlue};
    font-size: 20px;
    
    ${breakpoints.onlyMobile} {
        flex: 1;
        margin-right: 10px;
    }
`;

const ShortcutIndicator = styled.div<ShortcutIndicatorCSSProps>`
    ${fonts.indication};
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${(props) =>
        (props.showBackground && props.color) || colors.white};
    color: ${(props) => (props.showBackground && colors.white) || props.color};
    font-weight: bold;
    border-radius: 2px;
    color: ${(props) => props.textColor};
    margin-right: ${(props) => (props.margin ? 10 : 0)}px;
    
    ${breakpoints.onlyMobile} {
        margin-left: auto;
    }
`;

const ShortcutText = styled.div`
    padding: 5px 10px;
    box-sizing: border-box;
    text-align: center;
`;

const StyledNavLink = styled(NavLink)`
    text-decoration: none;
`;

const LoadingIndicator = styled.div<LoadingIndicatorCSSProps>`
    position: absolute;
    top: 0;
    z-index: 2;
    background: ${(props) => props.color};
    width: 100%;
    height: 100%;
    border-radius: 3px;
    display: ${(props) => (props.show ? 'block' : 'none')};
    @keyframes load {
        from {
            width: 0%;
        }
        to {
            width: 100%;
        }
    }
    animation: load 0.5s ease;
`;

const AnswerButtonBackgroundWrapper = styled.div`
    position: relative;
    align-self: stretch;
    display: inline-block;
    box-sizing: border-box;
`;

const AnswerButton: React.FC<AnswerButtonProps> = ({
    index,
    level,
    answerLevel,
    shortcut,
    color = 'black',
    backgroundType = 'shortcut',
    onClick,
    className,
    children,
    to,
    levels_state,
    loading,
    cursor = true,
    emailAuth,
    showShortcut = true,
}): React.ReactElement => {
    const ref = useRef<HTMLDivElement>(null);
    const { width } = useViewport();
    const { t } = useTranslation(['HelpPopin']);

    const ButtonContent = (
        <AnswerButtonBackgroundWrapper>
            <AnswerButtonWrapper
                className={className}
                shouldShowCursor={cursor}
                color={(backgroundType === 'content' && color) || 'transparent'}
                showBorder={backgroundType === 'shortcut'}
                onClick={(e) => onClick && onClick(e)}
                padding={level != null}
                ref={ref}
            >
                {children ||
                    (index && level != null && (
                        <Text>
                            {levels_state.levels_data[index - 1].answerLevel}
                        </Text>
                    ))}                {showShortcut && (
                    <ShortcutIndicator
                        margin={!level && level != 0}
                        color={color}
                        showBackground={backgroundType === 'shortcut'}
                        textColor={colors.libertyBlue}
                    >
                        <ShortcutText>{shortcut}</ShortcutText>
                    </ShortcutIndicator>
                )}
            </AnswerButtonWrapper>
            <LoadingIndicator show={!!loading} color={color} />
        </AnswerButtonBackgroundWrapper>
    );

    const shortcutCallBack = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === shortcut && !loading) ref.current?.click();
        },
        [onClick, shortcut, loading],
    );
    useEventListener('keydown', shortcutCallBack);
    return to ? (
        <StyledNavLink
            to={to}
            state={{ levels_state: levels_state, emailAuth: emailAuth }}
        >
            {ButtonContent}
        </StyledNavLink>
    ) : (
        <React.Fragment>{ButtonContent}</React.Fragment>
    );
};

export default AnswerButton;